import React, {
  memo,
  useEffect,
  useState,
  useCallback,
  useMemo,
  useRef,
} from 'react'
import { Helmet } from 'react-helmet'
import { message, Spin, Input, Tooltip } from 'antd'
import { inject, observer } from 'mobx-react'
import {
  PlayCircleFilled,
  FilePdfOutlined,
  FileTextOutlined,
  SearchOutlined,
} from '@ant-design/icons'
import Fuse from 'fuse.js'
import DashboardLayout from '../../layouts/DashboardLayout'
import PageTitle from '../../components/PageTitle'
import InfiniteScroll from 'react-infinite-scroll-component'
import PostComposer from './components/PostComposer/PostComposer'
import PostCard from './components/PostCard/PostCard'
import SkeletonPost from './components/SkeletonPost/SkeletonPost'

// --- Lazy Render Component to prevent DOM Bloat ---
const LazyPostWrapper = ({ children }) => {
  const [isVisible, setIsVisible] = useState(true)
  const [height, setHeight] = useState('auto')
  const ref = useRef()

  useEffect(() => {
    if (!ref.current) return
    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            setIsVisible(true)
          } else {
            // When leaving viewport, save the current height to prevent scroll jumping
            if (
              ref.current &&
              ref.current.getBoundingClientRect().height > 50
            ) {
              setHeight(ref.current.getBoundingClientRect().height)
            }
            setIsVisible(false)
          }
        })
      },
      { rootMargin: '1200px 0px' } // Load if within 1200px of viewport
    )
    observer.observe(ref.current)
    return () => observer.disconnect()
  }, [])

  return (
    <div
      ref={ref}
      style={{
        height: isVisible ? 'auto' : height,
        minHeight: 200,
        marginBottom: 16,
      }}>
      {isVisible ? children : null}
    </div>
  )
}
// ------------------------------------------------

import LightboxViewer from './components/LightboxViewer/LightboxViewer'
import FileViewPopup from '../../components/FileViewPopup/FileViewPopup'
import { apiUrl } from '../../config'

import moment from 'moment'
import {
  PageWrapper,
  PageContentInner,
  SidebarLeft,
  SidebarRight,
  SidebarCard,
  UserCard,
  UserAvatar as StyledUserAvatar,
  UserInfo,
  UserName,
  UserDept,
  SidebarMenu,
  SidebarItem,
  SidebarIcon,
  MainFeed,
  LoadingMore,
  EndMessage,
  EmptyState,
  RecentPhotosGrid,
  FeedPulseList,
  colors,
} from './NewsFeedStyled'
import utils from '../../utils'
import { getCleanPlainText } from './constants/newsFeedConstants'

const resolveMediaUrl = m => {
  if (!m) return null
  const url = m.fileUrl || m.file_url
  if (url) {
    if (url.startsWith('http')) return url
    if (url.startsWith('/')) return `${apiUrl}${url}`
    if (/^[0-9a-fA-F]{24}$/.test(url)) {
      return `${apiUrl}/api/v1/images/${url}`
    }
    return `${apiUrl}/${url}`
  }
  const id = m.fileId || m.file_id
  if (id) {
    return `${apiUrl}/api/v1/images/${id}`
  }
  return null
}

const resolveAvatarUrl = url => {
  if (!url) return null
  if (url.startsWith('http')) return url
  return `${apiUrl}${url}`
}

const SIDEBAR_MENU_ITEMS = [
  { key: 'feed', icon: '📰', label: 'Bảng tin' },
  { key: 'bookmarks', icon: '🔖', label: 'Đã lưu' },
  { key: 'hidden', icon: '🙈', label: 'Đã ẩn' },
  { key: 'photos', icon: '📸', label: 'Ảnh' },
  { key: 'videos', icon: '🎬', label: 'Media' },
  { key: 'documents', icon: '📁', label: 'Tài liệu' },
]

const NewsFeedPage = props => {
  const {
    newsFeedStore,
    authenticationStore,
    loadingAnimationStore,
    userStore,
    history,
  } = props
  const { feedPosts, feedHasMore, feedLoading, feedTotalCount } = newsFeedStore
  const currentUser = authenticationStore.currentUser

  const [initialLoading, setInitialLoading] = useState(true)
  const [searchKeyword, setSearchKeyword] = useState('')
  const [activeTab, setActiveTab] = useState('feed') // 'feed', 'bookmarks', 'photos', 'videos', 'documents'
  const [myMediaItems, setMyMediaItems] = useState([])
  const [mediaLoading, setMediaLoading] = useState(false)
  const [lightboxOpen, setLightboxOpen] = useState(false)
  const [lightboxIndex, setLightboxIndex] = useState(0)
  const [lightboxMedia, setLightboxMedia] = useState([])
  const [isScrolled, setIsScrolled] = useState(false)
  const [isVisiblePreview, setIsVisiblePreview] = useState(false)
  const [previewFile, setPreviewFile] = useState(null)
  const [visibleMediaCount, setVisibleMediaCount] = useState(10)

  const handlePreviewFile = useCallback(item => {
    setPreviewFile({
      file_name: item.fileName || item.file_name || 'Tài liệu không tên',
      file_id: item.fileId || item.file_id || item.id,
    })
    setIsVisiblePreview(true)
  }, [])

  // Listen to window scroll to raise SidebarLeft position
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 40) {
        setIsScrolled(true)
      } else {
        setIsScrolled(false)
      }
    }
    window.addEventListener('scroll', handleScroll, { passive: true })
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  // Load feed lần đầu + ảnh gần đây + user list (1 lần cho cả module)
  useEffect(() => {
    ;(async () => {
      try {
        setInitialLoading(true)
        await newsFeedStore.getFeed(true, activeTab)
        await newsFeedStore.getUnreadCount()
      } catch (err) {
        console.error('Load feed error:', err)
        message.error(err?.vi || 'Không thể tải bảng tin!')
      } finally {
        setInitialLoading(false)
      }
    })()
    ;(async () => {
      try {
        const items = await newsFeedStore.getMyMedia()
        setMyMediaItems(items || [])
      } catch (err) {
        console.error('Load recent media error:', err)
      }
    })()
    // User list cho mention / gắn thẻ — chỉ gọi 1 lần (store có dedupe)
    if (userStore) {
      userStore.getUserListV2().catch(err => {
        console.error('Load user list error:', err)
      })
      if (
        !userStore.mentionUserList ||
        userStore.mentionUserList.length === 0
      ) {
        userStore.getMentionUserList().catch(() => {})
      }
    }
    return () => {
      newsFeedStore.clearStore()
    }
  }, [])

  const handleTabChange = useCallback(
    async tab => {
      setActiveTab(tab)
      setSearchKeyword('')
      setVisibleMediaCount(10)
      if (tab === 'feed' || tab === 'bookmarks' || tab === 'hidden') {
        try {
          setInitialLoading(true)
          await newsFeedStore.getFeed(true, tab)
        } catch (err) {
          console.error('Tab change error:', err)
          message.error(err?.vi || 'Không thể tải dữ liệu!')
        } finally {
          setInitialLoading(false)
        }
      } else {
        try {
          setMediaLoading(true)
          const items = await newsFeedStore.getMyMedia()
          setMyMediaItems(items)
        } catch (err) {
          console.error('Load media error:', err)
          message.error('Không thể tải danh sách tệp đính kèm!')
        } finally {
          setMediaLoading(false)
        }
      }
    },
    [newsFeedStore, setSearchKeyword]
  )

  const fetchMorePosts = useCallback(async () => {
    if (!feedHasMore || feedLoading) return
    try {
      await newsFeedStore.getFeed(false, activeTab)
    } catch (err) {
      console.error('Fetch more error:', err)
      message.error(err?.vi || 'Không thể tải thêm bài viết!')
    }
  }, [feedHasMore, feedLoading, activeTab])

  const handlePostCreated = useCallback(() => {
    message.success('Đăng bài thành công!')
  }, [])

  const handleNavigateToPost = useCallback((postId, commentId) => {
    const postElement = document.getElementById(`post-${postId}`)
    if (postElement) {
      postElement.scrollIntoView({ behavior: 'smooth', block: 'center' })
      postElement.classList.add('nf-post-highlight')
      setTimeout(() => {
        postElement.classList.remove('nf-post-highlight')
      }, 2000)

      if (commentId) {
        setTimeout(() => {
          const commentElement = document.getElementById(`comment-${commentId}`)
          if (commentElement) {
            commentElement.scrollIntoView({
              behavior: 'smooth',
              block: 'center',
            })
            commentElement.classList.add('nf-comment-highlight')
            setTimeout(() => {
              commentElement.classList.remove('nf-comment-highlight')
            }, 2500)
          }
        }, 600)
      }
    }
  }, [])

  const { currentUserAvatar } = authenticationStore
  const userAvatarSrc = useMemo(
    () =>
      currentUserAvatar
        ? URL.createObjectURL(currentUserAvatar)
        : resolveAvatarUrl(currentUser?.avatarUrl || currentUser?.image_id),
    [currentUserAvatar, currentUser?.avatarUrl, currentUser?.image_id]
  )

  useEffect(() => {
    return () => {
      if (userAvatarSrc && userAvatarSrc.startsWith('blob:')) {
        URL.revokeObjectURL(userAvatarSrc)
      }
    }
  }, [userAvatarSrc])

  const fuse = useMemo(() => {
    const options = {
      keys: ['content', 'displayName'],
      threshold: 0.4,
      includeScore: true,
    }
    return new Fuse(feedPosts, options)
  }, [feedPosts])

  const filteredFeedPosts = useMemo(() => {
    if (!searchKeyword.trim()) return feedPosts
    const results = fuse.search(searchKeyword)
    return results.map(r => r.item)
  }, [searchKeyword, fuse])

  const filteredMyMediaItems = useMemo(() => {
    if (!searchKeyword.trim()) return myMediaItems
    const kw = searchKeyword.toLowerCase()
    return myMediaItems.filter(item => {
      const name = (item.fileName || item.file_name || '').toLowerCase()
      return name.includes(kw)
    })
  }, [myMediaItems, searchKeyword])

  const { photos, videos, documents } = useMemo(() => {
    const ph = []
    const vi = []
    const doc = []
    for (const item of filteredMyMediaItems) {
      const type = item.mediaType || item.media_type
      if (type === 'IMAGE') {
        ph.push(item)
      } else if (type === 'VIDEO') {
        vi.push(item)
      } else if (type === 'DOCUMENT' || type === 'PDF') {
        doc.push(item)
      }
    }
    return { photos: ph, videos: vi, documents: doc }
  }, [filteredMyMediaItems])

  const handleSidebarItemClick = useCallback(
    key => {
      handleTabChange(key)
    },
    [handleTabChange]
  )

  const sidebarStyle = useMemo(
    () => ({
      top: isScrolled ? 15 : 50,
      transition: 'top 0.25s ease-in-out',
    }),
    [isScrolled]
  )

  const sidebarMenuContent = useMemo(() => {
    return SIDEBAR_MENU_ITEMS.map(item => (
      <SidebarItem
        key={item.key}
        active={activeTab === item.key}
        onClick={() => handleSidebarItemClick(item.key)}>
        <SidebarIcon>{item.icon}</SidebarIcon>
        <span>{item.label}</span>
      </SidebarItem>
    ))
  }, [activeTab, handleSidebarItemClick])

  const recentPhotos = useMemo(() => {
    return myMediaItems
      .filter(m => (m.mediaType || m.media_type) === 'IMAGE')
      .slice(0, 6)
  }, [myMediaItems])

  const recentPhotoTotal = useMemo(() => {
    return myMediaItems.filter(m => (m.mediaType || m.media_type) === 'IMAGE')
      .length
  }, [myMediaItems])

  const feedPulse = useMemo(() => {
    return (feedPosts || []).slice(0, 4)
  }, [feedPosts])

  const openRecentPhoto = useCallback((items, index) => {
    setLightboxMedia(items)
    setLightboxIndex(index)
    setLightboxOpen(true)
  }, [])

  const scrollToPost = useCallback(postId => {
    const el = document.getElementById(`post-${postId}`)
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' })
      el.classList.add('nf-post-highlight')
      setTimeout(() => el.classList.remove('nf-post-highlight'), 2000)
    } else {
      setActiveTab('feed')
    }
  }, [])

  return (
    <DashboardLayout title="Bảng tin">
      <Helmet>
        <title>Bảng tin</title>
      </Helmet>
      <PageTitle
        location={props.location}
        title="Bảng tin"
        hiddenGoBack={true}
      />
      <PageWrapper>
        <PageContentInner>
          {/* Cột trái — profile + ảnh gần đây + nhịp bảng tin */}
          <SidebarLeft style={sidebarStyle}>
            <UserCard>
              <StyledUserAvatar>
                {userAvatarSrc ? (
                  <img src={userAvatarSrc} alt="" />
                ) : (
                  (
                    currentUser?.name_uppercase ||
                    currentUser?.fullName ||
                    currentUser?.username ||
                    'U'
                  )
                    .toString()
                    .charAt(0)
                    .toUpperCase()
                )}
              </StyledUserAvatar>
              <UserInfo>
                <UserName>
                  {utils.getNameInCapitalize(
                    currentUser?.name_uppercase ||
                      currentUser?.full_name ||
                      currentUser?.username ||
                      'Người dùng'
                  )}
                </UserName>
                <Tooltip
                  title={
                    currentUser?.department?.name ||
                    currentUser?.company?.name ||
                    'VIMC Logistics'
                  }>
                  <UserDept>
                    {currentUser?.department?.name ||
                      currentUser?.company?.name ||
                      'VIMC Logistics'}
                  </UserDept>
                </Tooltip>
              </UserInfo>
            </UserCard>

            <SidebarCard>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: 12,
                }}>
                <div
                  style={{
                    fontWeight: 700,
                    fontSize: 15,
                    color: colors.textPrimary,
                  }}>
                  Ảnh gần đây
                </div>
                {recentPhotoTotal > 0 && (
                  <button
                    type="button"
                    onClick={() => handleSidebarItemClick('photos')}
                    style={{
                      border: 'none',
                      background: 'transparent',
                      color: colors.primary,
                      fontSize: 12,
                      fontWeight: 600,
                      cursor: 'pointer',
                      padding: 0,
                    }}>
                    Xem tất cả
                  </button>
                )}
              </div>
              {recentPhotos.length === 0 ? (
                <div
                  style={{
                    fontSize: 13,
                    color: colors.textSecondary,
                    lineHeight: 1.5,
                  }}>
                  Chưa có ảnh nào. Đăng bài kèm ảnh để xem tại đây.
                </div>
              ) : (
                <RecentPhotosGrid>
                  {recentPhotos.map((m, idx) => {
                    const src = resolveMediaUrl(m)
                    const showMore =
                      idx === recentPhotos.length - 1 && recentPhotoTotal > 6
                    return (
                      <div
                        key={m.id || idx}
                        className="nf-recent-photo"
                        onClick={() => {
                          if (showMore) {
                            handleSidebarItemClick('photos')
                          } else {
                            openRecentPhoto(recentPhotos, idx)
                          }
                        }}>
                        {src && <img src={src} alt="" loading="lazy" />}
                        {showMore && (
                          <div className="nf-recent-photo-more">
                            +{recentPhotoTotal - 5}
                          </div>
                        )}
                      </div>
                    )
                  })}
                </RecentPhotosGrid>
              )}
            </SidebarCard>

            <SidebarCard>
              <div
                style={{
                  fontWeight: 700,
                  fontSize: 15,
                  color: colors.textPrimary,
                  marginBottom: 12,
                }}>
                Đang diễn ra
              </div>
              {feedPulse.length === 0 ? (
                <div style={{ fontSize: 13, color: colors.textSecondary }}>
                  Chưa có bài viết mới trên bảng tin.
                </div>
              ) : (
                <FeedPulseList>
                  {feedPulse.map(post => {
                    const name =
                      post.display_name || post.displayName || 'Người dùng'
                    const avatar = resolveAvatarUrl(
                      post.avatar_url || post.avatarUrl
                    )
                    let feelingEmoji = ''
                    if (post.content && post.content.includes('<!--feeling:')) {
                      try {
                        const m = post.content.match(/<!--feeling:(.*?)-->/)
                        if (m) {
                          const f = JSON.parse(m[1])
                          if (f?.emoji) feelingEmoji = f.emoji
                        }
                      } catch (e) {}
                    } else if (post.feeling) {
                      try {
                        const f =
                          typeof post.feeling === 'string'
                            ? JSON.parse(post.feeling)
                            : post.feeling
                        if (f?.emoji) feelingEmoji = f.emoji
                      } catch (e) {}
                    }

                    let content = getCleanPlainText(post.content)
                    if (feelingEmoji && !content.startsWith(feelingEmoji)) {
                      content = `${feelingEmoji} ${content}`
                    }
                    const time = post.created_at || post.createdAt
                    return (
                      <div
                        key={post.id}
                        className="nf-pulse-item"
                        onClick={() => scrollToPost(post.id)}>
                        <div className="nf-pulse-avatar">
                          {avatar ? (
                            <img src={avatar} alt="" />
                          ) : (
                            name.charAt(0).toUpperCase()
                          )}
                        </div>
                        <div className="nf-pulse-body">
                          <div className="nf-pulse-name">{name}</div>
                          <div className="nf-pulse-text">
                            {content ||
                              (post.media?.length
                                ? `Đã đăng ${post.media.length} tệp đính kèm`
                                : 'Đã đăng một bài viết')}
                          </div>
                          {time && (
                            <div className="nf-pulse-time">
                              {moment(time).fromNow()}
                            </div>
                          )}
                        </div>
                      </div>
                    )
                  })}
                </FeedPulseList>
              )}
            </SidebarCard>
          </SidebarLeft>

          {/* Cột giữa — Feed */}
          <MainFeed>
            {/* Composer visible only on feed tab */}
            {activeTab === 'feed' && (
              <PostComposer
                currentUser={currentUser}
                onPostCreated={handlePostCreated}
              />
            )}

            <style>{`
            .nf-media-card:hover {
              transform: scale(1.04);
              box-shadow: 0 8px 24px rgba(0,0,0,0.12) !important;
            }
            .nf-document-item:hover {
              background: #f8f9fa !important;
              border-color: #ccd0d5 !important;
              box-shadow: 0 4px 12px rgba(0,0,0,0.06) !important;
            }
            .nf-document-item:hover .nf-document-download-btn {
              background: #e4e6eb !important;
            }
            .nf-document-item:hover .nf-document-preview-btn {
              background: #dbeafe !important;
            }

            @keyframes nfPulse {
              0% { opacity: 0.5; }
              50% { opacity: 1; }
              100% { opacity: 0.5; }
            }
            .nf-skeleton-pulse {
              animation: nfPulse 1.5s infinite ease-in-out;
            }
            @keyframes nfShimmer {
              0% { background-position: 200% 0; }
              100% { background-position: -200% 0; }
            }
          `}</style>

            {/* Feed & Bookmarks Tabs */}
            {(activeTab === 'feed' ||
              activeTab === 'bookmarks' ||
              activeTab === 'hidden') &&
              (initialLoading ? (
                <div
                  style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                  <SkeletonPost />
                  <SkeletonPost />
                  <SkeletonPost />
                </div>
              ) : (
                <InfiniteScroll
                  dataLength={filteredFeedPosts.length}
                  next={fetchMorePosts}
                  hasMore={searchKeyword.trim() ? false : feedHasMore}
                  scrollableTarget="infinityScrollableDiv"
                  loader={
                    <LoadingMore>
                      <Spin tip="Đang tải thêm..." />
                    </LoadingMore>
                  }
                  endMessage={
                    filteredFeedPosts.length > 0 ? (
                      <EndMessage>
                        <div className="icon">✨</div>
                        <p>
                          {activeTab === 'bookmarks'
                            ? 'Bạn đã xem hết các bài viết đã lưu!'
                            : activeTab === 'hidden'
                            ? 'Bạn đã xem hết các bài viết đã ẩn!'
                            : 'Bạn đã xem hết bảng tin!'}
                        </p>
                      </EndMessage>
                    ) : null
                  }>
                  {filteredFeedPosts.length === 0 && !feedLoading ? (
                    <EmptyState>
                      <div style={{ fontSize: 40, marginBottom: 16 }}>
                        {searchKeyword.trim()
                          ? '🔍'
                          : activeTab === 'bookmarks'
                          ? '🔖'
                          : activeTab === 'hidden'
                          ? '🙈'
                          : '📝'}
                      </div>
                      <h3>
                        {searchKeyword.trim()
                          ? 'Không tìm thấy kết quả'
                          : activeTab === 'bookmarks'
                          ? 'Chưa có bài viết đã lưu nào'
                          : activeTab === 'hidden'
                          ? 'Chưa có bài viết đã ẩn nào'
                          : 'Chưa có bài viết nào'}
                      </h3>
                      <p>
                        {searchKeyword.trim()
                          ? 'Thử tìm kiếm với từ khóa khác.'
                          : activeTab === 'bookmarks'
                          ? 'Hãy lưu các bài viết thú vị để đọc lại sau!'
                          : activeTab === 'hidden'
                          ? 'Bài viết bạn ẩn đi sẽ xuất hiện tại đây.'
                          : 'Hãy là người đầu tiên chia sẻ điều gì đó!'}
                      </p>
                    </EmptyState>
                  ) : (
                    filteredFeedPosts.map(post => (
                      <LazyPostWrapper key={post.id}>
                        <PostCard
                          post={post}
                          currentUser={currentUser}
                          newsFeedStore={newsFeedStore}
                          authenticationStore={authenticationStore}
                          isBookmarksTab={activeTab === 'bookmarks'}
                        />
                      </LazyPostWrapper>
                    ))
                  )}
                </InfiniteScroll>
              ))}

            {/* Photos Tab */}
            {activeTab === 'photos' &&
              (mediaLoading ? (
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns:
                      'repeat(auto-fill, minmax(150px, 1fr))',
                    gap: 12,
                  }}>
                  {Array.from({ length: 9 }).map((_, i) => (
                    <div
                      key={i}
                      className="nf-skeleton-pulse"
                      style={{
                        paddingTop: '100%',
                        borderRadius: 12,
                        background:
                          'linear-gradient(90deg, #e8eaed 25%, #f3f4f6 50%, #e8eaed 75%)',
                        backgroundSize: '200% 100%',
                        animation: 'nfShimmer 1.4s infinite',
                      }}
                    />
                  ))}
                </div>
              ) : photos.length === 0 ? (
                <EmptyState>
                  <div style={{ fontSize: 40, marginBottom: 16 }}>
                    {searchKeyword.trim() ? '🔍' : '📸'}
                  </div>
                  <h3>
                    {searchKeyword.trim()
                      ? 'Không tìm thấy kết quả'
                      : 'Chưa có ảnh nào'}
                  </h3>
                  <p>
                    {searchKeyword.trim()
                      ? 'Thử tìm kiếm với từ khóa khác.'
                      : 'Bạn chưa tải lên bất kỳ hình ảnh nào!'}
                  </p>
                </EmptyState>
              ) : (
                <div>
                  <h3
                    style={{
                      fontSize: 18,
                      fontWeight: 700,
                      color: '#050505',
                      marginBottom: 4,
                    }}>
                    Tất cả ảnh đã đăng
                  </h3>
                  <p
                    style={{
                      color: '#65676b',
                      fontSize: 13,
                      marginBottom: 16,
                    }}>
                    Tổng số {photos.length} hình ảnh
                  </p>
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns:
                        'repeat(auto-fill, minmax(150px, 1fr))',
                      gap: 12,
                    }}>
                    {photos.slice(0, visibleMediaCount).map((item, idx) => (
                      <div
                        key={item.id || idx}
                        style={{
                          position: 'relative',
                          paddingTop: '100%',
                          borderRadius: 12,
                          overflow: 'hidden',
                          cursor: 'pointer',
                          boxShadow: '0 4px 12px rgba(0,0,0,0.06)',
                          background: '#f0f2f5',
                          transition: 'all 0.2s ease',
                          height: 200,
                          maxHeight: 200,
                        }}
                        className="nf-media-card"
                        onClick={() => {
                          setLightboxMedia(photos.slice(0, visibleMediaCount))
                          setLightboxIndex(idx)
                          setLightboxOpen(true)
                        }}>
                        <img
                          src={resolveMediaUrl(item)}
                          alt={item.fileName}
                          style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                          }}
                        />
                      </div>
                    ))}
                    {photos.length > visibleMediaCount && (
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          borderRadius: 12,
                          background: '#f0f2f5',
                          cursor: 'pointer',
                          boxShadow: '0 4px 12px rgba(0,0,0,0.06)',
                          transition: 'all 0.2s ease',
                          height: 200,
                          maxHeight: 200,
                          fontWeight: 600,
                          color: '#65676b',
                        }}
                        className="nf-media-card"
                        onClick={() => setVisibleMediaCount(prev => prev + 10)}>
                        Xem thêm{' '}
                        {Math.min(10, photos.length - visibleMediaCount)}
                      </div>
                    )}
                  </div>
                </div>
              ))}

            {/* Videos Tab */}
            {activeTab === 'videos' &&
              (mediaLoading ? (
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns:
                      'repeat(auto-fill, minmax(180px, 1fr))',
                    gap: 12,
                  }}>
                  {Array.from({ length: 6 }).map((_, i) => (
                    <div
                      key={i}
                      className="nf-skeleton-pulse"
                      style={{
                        borderRadius: 12,
                        aspectRatio: '16/9',
                        background:
                          'linear-gradient(90deg, #e8eaed 25%, #f3f4f6 50%, #e8eaed 75%)',
                        backgroundSize: '200% 100%',
                        animation: 'nfShimmer 1.4s infinite',
                        animationDelay: `${i * 0.1}s`,
                      }}
                    />
                  ))}
                </div>
              ) : videos.length === 0 ? (
                <EmptyState>
                  <div style={{ fontSize: 40, marginBottom: 16 }}>
                    {searchKeyword.trim() ? '🔍' : '🎬'}
                  </div>
                  <h3>
                    {searchKeyword.trim()
                      ? 'Không tìm thấy kết quả'
                      : 'Chưa có video nào'}
                  </h3>
                  <p>
                    {searchKeyword.trim()
                      ? 'Thử tìm kiếm với từ khóa khác.'
                      : 'Bạn chưa tải lên bất kỳ video nào!'}
                  </p>
                </EmptyState>
              ) : (
                <div>
                  <h3
                    style={{
                      fontSize: 18,
                      fontWeight: 700,
                      color: '#050505',
                      marginBottom: 4,
                    }}>
                    Tất cả video/media đã đăng
                  </h3>
                  <p
                    style={{
                      color: '#65676b',
                      fontSize: 13,
                      marginBottom: 16,
                    }}>
                    Tổng số {videos.length} video
                  </p>
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns:
                        'repeat(auto-fill, minmax(180px, 1fr))',
                      gap: 12,
                    }}>
                    {videos.slice(0, visibleMediaCount).map((item, idx) => (
                      <div
                        key={item.id || idx}
                        style={{
                          borderRadius: 12,
                          overflow: 'hidden',
                          cursor: 'pointer',
                          boxShadow: '0 4px 12px rgba(0,0,0,0.06)',
                          background: '#000',
                          aspectRatio: '16/9',
                          position: 'relative',
                          transition: 'all 0.2s ease',
                        }}
                        className="nf-media-card"
                        onClick={() => {
                          setLightboxMedia(videos.slice(0, visibleMediaCount))
                          setLightboxIndex(idx)
                          setLightboxOpen(true)
                        }}>
                        <video
                          src={resolveMediaUrl(item)}
                          style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                          }}
                          muted
                          disabled
                        />
                        <div
                          style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            background: 'rgba(0,0,0,0.25)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}>
                          <PlayCircleFilled
                            style={{
                              color: '#fff',
                              fontSize: 36,
                              opacity: 0.9,
                            }}
                          />
                        </div>
                      </div>
                    ))}
                    {videos.length > visibleMediaCount && (
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          borderRadius: 12,
                          background: '#f0f2f5',
                          cursor: 'pointer',
                          boxShadow: '0 4px 12px rgba(0,0,0,0.06)',
                          transition: 'all 0.2s ease',
                          aspectRatio: '16/9',
                          fontWeight: 600,
                          color: '#65676b',
                        }}
                        className="nf-media-card"
                        onClick={() => setVisibleMediaCount(prev => prev + 10)}>
                        Xem thêm{' '}
                        {Math.min(10, videos.length - visibleMediaCount)}
                      </div>
                    )}
                  </div>
                </div>
              ))}

            {/* Documents Tab */}
            {activeTab === 'documents' &&
              (mediaLoading ? (
                <div
                  style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  {Array.from({ length: 4 }).map((_, i) => (
                    <div
                      key={i}
                      className="nf-skeleton-pulse"
                      style={{
                        height: 80,
                        borderRadius: 12,
                        background:
                          'linear-gradient(90deg, #e8eaed 25%, #f3f4f6 50%, #e8eaed 75%)',
                        backgroundSize: '200% 100%',
                        animation: 'nfShimmer 1.4s infinite',
                        animationDelay: `${i * 0.15}s`,
                      }}
                    />
                  ))}
                </div>
              ) : documents.length === 0 ? (
                <EmptyState>
                  <div style={{ fontSize: 40, marginBottom: 16 }}>
                    {searchKeyword.trim() ? '🔍' : '📁'}
                  </div>
                  <h3>
                    {searchKeyword.trim()
                      ? 'Không tìm thấy kết quả'
                      : 'Chưa có tài liệu nào'}
                  </h3>
                  <p>
                    {searchKeyword.trim()
                      ? 'Thử tìm kiếm với từ khóa khác.'
                      : 'Bạn chưa tải lên bất kỳ tài liệu nào!'}
                  </p>
                </EmptyState>
              ) : (
                <div>
                  <h3
                    style={{
                      fontSize: 18,
                      fontWeight: 700,
                      color: '#050505',
                      marginBottom: 4,
                    }}>
                    Tất cả tài liệu đã đăng
                  </h3>
                  <p
                    style={{
                      color: '#65676b',
                      fontSize: 13,
                      marginBottom: 16,
                    }}>
                    Tổng số {documents.length} tài liệu
                  </p>
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 12,
                    }}>
                    {documents.slice(0, visibleMediaCount).map((item, idx) => {
                      const src = resolveMediaUrl(item)
                      const isPdf =
                        item.mediaType === 'PDF' ||
                        item.media_type === 'PDF' ||
                        (item.fileName || item.file_name)
                          ?.toLowerCase()
                          .endsWith('.pdf')
                      const displayName =
                        item.fileName || item.file_name || 'Tài liệu không tên'
                      const fileSize = item.fileSize || item.file_size
                      const fileSizeFormatted = fileSize
                        ? `${(fileSize / (1024 * 1024)).toFixed(2)} MB`
                        : 'Không rõ dung lượng'
                      const createdAt = item.createdAt || item.created_at
                      const dateFormatted = createdAt
                        ? moment(createdAt).format('DD/MM/YYYY [lúc] HH:mm')
                        : ''
                      return (
                        <div
                          key={item.id || idx}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            padding: '16px 20px',
                            background: '#fff',
                            borderRadius: 12,
                            border: '1px solid #e4e6eb',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.02)',
                            transition: 'all 0.2s ease',
                            cursor: 'pointer',
                          }}
                          className="nf-document-item"
                          onClick={() => handlePreviewFile(item)}>
                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 16,
                            }}>
                            <div
                              style={{
                                width: 44,
                                height: 44,
                                borderRadius: 10,
                                background: isPdf ? '#fef2f2' : '#eff6ff',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                              }}>
                              {isPdf ? (
                                <FilePdfOutlined
                                  style={{ color: '#ef4444', fontSize: 22 }}
                                />
                              ) : (
                                <FileTextOutlined
                                  style={{ color: '#3b82f6', fontSize: 22 }}
                                />
                              )}
                            </div>
                            <div
                              style={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 2,
                              }}>
                              <span
                                style={{
                                  fontWeight: 600,
                                  color: '#050505',
                                  fontSize: 14,
                                }}>
                                {displayName}
                              </span>
                              <span style={{ fontSize: 12, color: '#65676b' }}>
                                {fileSizeFormatted}{' '}
                                {dateFormatted &&
                                  `• Tải lên ngày ${dateFormatted}`}
                              </span>
                            </div>
                          </div>
                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 10,
                            }}>
                            <div
                              style={{
                                padding: '6px 16px',
                                borderRadius: 20,
                                background: '#eff6ff',
                                color: '#3b82f6',
                                fontSize: 13,
                                fontWeight: 600,
                                transition: 'all 0.2s ease',
                              }}
                              className="nf-document-preview-btn"
                              onClick={e => {
                                e.stopPropagation()
                                handlePreviewFile(item)
                              }}>
                              Xem trước
                            </div>
                            <div
                              style={{
                                padding: '6px 16px',
                                borderRadius: 20,
                                background: '#f0f2f5',
                                color: '#050505',
                                fontSize: 13,
                                fontWeight: 600,
                                transition: 'all 0.2s ease',
                              }}
                              className="nf-document-download-btn"
                              onClick={e => {
                                e.stopPropagation()
                                window.open(src, '_blank')
                              }}>
                              Tải xuống
                            </div>
                          </div>
                        </div>
                      )
                    })}
                    {documents.length > visibleMediaCount && (
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          padding: '12px 20px',
                          background: '#f0f2f5',
                          borderRadius: 12,
                          border: '1px dashed #ccd0d5',
                          cursor: 'pointer',
                          fontWeight: 600,
                          color: '#65676b',
                          transition: 'all 0.2s ease',
                        }}
                        className="nf-document-item"
                        onClick={() => setVisibleMediaCount(prev => prev + 10)}>
                        Xem thêm{' '}
                        {Math.min(10, documents.length - visibleMediaCount)} tài
                        liệu
                      </div>
                    )}
                  </div>
                </div>
              ))}
          </MainFeed>

          {/* Cột phải — tìm kiếm + menu */}
          <SidebarRight style={sidebarStyle}>
            <SidebarMenu>
              <div
                style={{
                  padding: '0 4px 10px 4px',
                  marginBottom: 8,
                }}>
                <Input
                  placeholder="Tìm kiếm bài viết..."
                  prefix={<SearchOutlined style={{ marginRight: 4 }} />}
                  value={searchKeyword}
                  onChange={e => {
                    setSearchKeyword(e.target.value)
                    setVisibleMediaCount(10)
                  }}
                  allowClear
                  style={{
                    borderRadius: 20,
                    border: '1px solid transparent',
                    padding: '6px 12px',
                    fontSize: 13,
                    width: '100%',
                    transition: 'all 0.2s',
                  }}
                />
              </div>
              {sidebarMenuContent}
            </SidebarMenu>
          </SidebarRight>
        </PageContentInner>

        {/* Lightbox Viewer for Photos/Videos tab */}
        {lightboxOpen && (
          <LightboxViewer
            media={lightboxMedia}
            initialIndex={lightboxIndex}
            onClose={() => setLightboxOpen(false)}
          />
        )}

        {/* File View Popup */}
        {isVisiblePreview && previewFile && (
          <FileViewPopup
            isVisibleFileViewPopup={isVisiblePreview}
            handleCloseFileViewPopup={() => setIsVisiblePreview(false)}
            file={previewFile}
          />
        )}
      </PageWrapper>
    </DashboardLayout>
  )
}

export default inject(
  'newsFeedStore',
  'authenticationStore',
  'loadingAnimationStore',
  'userStore'
)(observer(NewsFeedPage))
